/**
 * `fetchApi` / `fetchJson` / `ApiHttpError` / `readApiError` — the canonical
 * HTTP client for talking to apps/api (http://localhost:8788/api/* in dev,
 * PUKU_PCB_HOST/api/* in prod).
 *
 * Stage 1 ships the dev-mode helpers. Later stages add webhook-style helpers
 * (`fetchEventSource` in Stage 3) and Idempotency-Key support.
 */

const DEFAULT_HOST = "http://localhost:8788";

function resolveBase(): string {
  // Vite-injected env (set in vite.config.ts) — falls back to localhost in dev.
  return (import.meta.env.VITE_PUKU_PCB_HOST as string | undefined) ?? `${DEFAULT_HOST}/api`;
}

export class ApiHttpError extends Error {
  constructor(
    readonly status: number,
    readonly payload: unknown,
    readonly url: string
  ) {
    super(`api ${status} on ${url}`);
    this.name = "ApiHttpError";
  }
}

export async function readApiError(res: Response): Promise<unknown> {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    return text || res.statusText;
  }
}

/**
 * Raw fetch — returns the Response so callers can stream / peek headers.
 */
export function fetchApi(path: string, init: RequestInit = {}): Promise<Response> {
  const url = path.startsWith("http") ? path : `${resolveBase()}${path}`;
  return fetch(url, {
    ...init,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(init.body && !(init.body instanceof FormData)
        ? { "Content-Type": "application/json" }
        : {}),
      ...init.headers,
    },
  });
}

/**
 * JSON fetch — parses the body or throws `ApiHttpError`.
 */
export async function fetchJson<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetchApi(path, init);
  if (!res.ok) {
    throw new ApiHttpError(res.status, await readApiError(res), res.url);
  }
  return (await res.json()) as T;
}

/**
 * Minimal SSE reader. The browser's `EventSource` doesn't let us POST or
 * send custom headers, so we roll our own. Returns an `unsubscribe()`
 * handle that closes the underlying stream.
 */
export interface FetchEventSourceHandlers {
  onOpen?: () => void;
  onEvent: (event: { event: string; data: unknown; id?: string }) => void;
  onError?: (err: Error) => void;
}

export function fetchEventSource(path: string, handlers: FetchEventSourceHandlers): () => void {
  const url = path.startsWith("http") ? path : `${resolveBase()}${path}`;
  const controller = new AbortController();

  (async () => {
    try {
      const res = await fetch(url, {
        credentials: "include",
        headers: { Accept: "text/event-stream" },
        signal: controller.signal,
      });
      if (!res.ok || !res.body) {
        throw new ApiHttpError(res.status, await readApiError(res), res.url);
      }
      handlers.onOpen?.();

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE messages are terminated by a blank line.
        const frames = buffer.split("\n\n");
        buffer = frames.pop() ?? "";
        for (const frame of frames) {
          let event = "message";
          let data = "";
          let id: string | undefined;
          for (const line of frame.split("\n")) {
            if (line.startsWith(":")) continue; // comment / heartbeat
            if (line.startsWith("event:")) event = line.slice(6).trim();
            else if (line.startsWith("data:")) data += line.slice(5).trim();
            else if (line.startsWith("id:")) id = line.slice(3).trim();
          }
          if (!data) continue;
          let parsed: unknown = data;
          try {
            parsed = JSON.parse(data);
          } catch {
            // keep raw string
          }
          handlers.onEvent({ event, data: parsed, id });
        }
      }
    } catch (err) {
      if ((err as { name?: string })?.name === "AbortError") return;
      handlers.onError?.(err instanceof Error ? err : new Error(String(err)));
    }
  })();

  return () => controller.abort();
}
